/**
 * Service-address normalizer for the web-quote flow.
 *
 * The publisher CF pulls raw `addr.line1`, `addr.city`, `addr.state`,
 * `addr.postal_code` from Zendesk Sell and used to emit only the
 * comma-joined display string `quote.address`. That string was then
 * re-split by position on the viewer side, which broke whenever a Sell
 * contact was missing the city or the zip (or had a stuffed line1 like
 * "Street, , CA, USA"). See `memory/feedback_web_quote_demers.md` for
 * the deal 199482040 incident.
 *
 * The fix: publisher emits `serviceAddressFields` as a structured object
 * after running `cleanServiceAddressFields` below. The viewer normalizes
 * with `normalizeServiceAddress`, which validates shape and falls back
 * to null when the structured object isn't usable so callers can decide
 * whether to fall back to legacy parsing of `quote.address` or treat the
 * prefill as missing.
 *
 * `normalizeServiceAddress` is also used in the React form. The two
 * runtime locations (`api/_lib` and `src/utils`) keep duplicate copies
 * because CRA does not allow importing from outside `src/`.
 */

const STATE_NAME_TO_ABBREV = Object.freeze({
  'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
  'colorado':'CO','connecticut':'CT','delaware':'DE','district of columbia':'DC',
  'florida':'FL','georgia':'GA','hawaii':'HI','idaho':'ID','illinois':'IL',
  'indiana':'IN','iowa':'IA','kansas':'KS','kentucky':'KY','louisiana':'LA',
  'maine':'ME','maryland':'MD','massachusetts':'MA','michigan':'MI','minnesota':'MN',
  'mississippi':'MS','missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV',
  'new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY',
  'north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK','oregon':'OR',
  'pennsylvania':'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD',
  'tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT','virginia':'VA',
  'washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
});

const STATE_ABBREVS = new Set(Object.values(STATE_NAME_TO_ABBREV));

const ZIP_RE = /^\d{5}(-\d{4})?$/;

export function normalizeState(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (s.length === 2 && STATE_ABBREVS.has(s.toUpperCase())) return s.toUpperCase();
  const looked = STATE_NAME_TO_ABBREV[s.toLowerCase()];
  return looked || '';
}

export function normalizeZip(raw) {
  const s = String(raw || '').trim();
  if (ZIP_RE.test(s)) return s;
  const digitsOnly = s.replace(/\D/g, '');
  if (digitsOnly.length === 5) return digitsOnly;
  if (digitsOnly.length === 9) return `${digitsOnly.slice(0,5)}-${digitsOnly.slice(5)}`;
  return '';
}

function stripTrailingPunct(s) {
  return String(s || '').replace(/[\s,]+$/g, '').trim();
}

const COUNTRY_TOKENS = new Set([
  'us', 'usa', 'u.s.', 'u.s.a.', 'united states', 'united states of america',
  'canada', 'ca.', 'mx', 'mexico',
]);

function looksLikeState(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  if (s.length === 2 && STATE_ABBREVS.has(s.toUpperCase())) return true;
  return s.toLowerCase() in STATE_NAME_TO_ABBREV;
}

function looksLikeStateOrCountry(raw) {
  const s = String(raw || '').trim();
  if (!s) return false;
  if (looksLikeState(s)) return true;
  return COUNTRY_TOKENS.has(s.toLowerCase());
}

export function cleanServiceAddressFields(input) {
  const o = input && typeof input === 'object' ? input : {};
  // Idempotency: if the input already carries a `raw` sub-object (i.e.,
  // this function ran once on the publisher side and the result is now
  // being re-validated on the API side), trust `raw` as the source of
  // truth and clean from there. Otherwise the top-level fields ARE the
  // original. Without this branch, the second pass would treat the
  // cleaned fields as raw inputs and lose the original Sell line1 for
  // downstream diagnostics.
  const source = (o.raw && typeof o.raw === 'object') ? o.raw : o;
  const rawStreet = String(source.street || '').trim();
  const rawCity = String(source.city || '').trim();
  const rawState = String(source.state || '').trim();
  const rawZip = String(source.zip || '').trim();

  // Iteratively strip trailing comma-separated tokens from line1 that
  // look like a state, country, or empty fragment. Real-world Sell
  // contacts have shown "Street, , CA, USA" (deal 199482040) where the
  // city slot was filled separately and the line1 got stuffed with the
  // tail of a previous data-entry attempt.
  // A full one-line address in the street field ("3633 Monte Real,
  // Escondido, CA 92029", deal 203055484) also loses a "ST 12345" tail and,
  // after something was stripped, a tail equal to the city.
  let street = stripTrailingPunct(rawStreet);
  let stripped = false;
  // A stripped "ST 12345" tail fills a state or ZIP field that is empty, so
  // the display address never loses them.
  let tailState = '';
  let tailZip = '';
  for (let i = 0; i < 6; i += 1) {
    const m = street.match(/,\s*([^,]*)\s*$/);
    if (!m) break;
    const tail = m[1].trim();
    const stateZip = /^[A-Za-z]{2}\s+\d{5}(?:-\d{4})?$/.test(tail) && looksLikeStateOrCountry(tail.split(/\s+/)[0]);
    const cityTail = stripped && rawCity && tail.toLowerCase() === stripTrailingPunct(rawCity).toLowerCase();
    if (tail === '' || looksLikeStateOrCountry(tail) || stateZip || cityTail) {
      if (stateZip && !tailZip) [tailState, tailZip] = tail.split(/\s+/);
      street = stripTrailingPunct(street.slice(0, m.index));
      stripped = true;
      // The city is the last locality token; what precedes it is street
      // ("123 Peachtree St, NE, Atlanta, GA 30303" keeps its "NE").
      if (cityTail) break;
    } else {
      break;
    }
  }

  let city = stripTrailingPunct(rawCity);
  if (looksLikeState(city)) city = '';

  const state = normalizeState(rawState) || normalizeState(tailState);
  const zip = normalizeZip(rawZip) || normalizeZip(tailZip);

  return {
    street,
    city,
    state,
    zip,
    raw: { street: rawStreet, city: rawCity, state: rawState, zip: rawZip },
  };
}

export function buildDisplayAddress(fields) {
  const f = fields || {};
  const tail = [f.state, f.zip].filter(Boolean).join(' ').trim();
  return [f.street, f.city, tail].filter(Boolean).join(', ');
}

export function isUsableServiceAddress(fields) {
  const f = fields || {};
  return Boolean(
    f.street && String(f.street).trim() &&
    f.city && String(f.city).trim() &&
    f.zip && ZIP_RE.test(String(f.zip).trim())
  );
}

export function normalizeServiceAddress(maybe) {
  if (!maybe || typeof maybe !== 'object' || Array.isArray(maybe)) return null;
  const cleaned = cleanServiceAddressFields(maybe);
  if (!cleaned.street && !cleaned.city && !cleaned.state && !cleaned.zip) return null;
  return cleaned;
}

export { STATE_NAME_TO_ABBREV, STATE_ABBREVS };
